import { useState } from 'react'
import { Eye } from 'lucide-react'
import { Button, Chip, Banner, useToast } from '../ds/index'
import { Icon } from './Icon'
import { useApi } from '../lib/useApi'
import { api, post } from '../lib/api'
import { ago } from '../lib/format'
import type { Subscriber } from '../lib/types'

type EmailRow = { id: string; kind: string; status: 'pending' | 'sent' | 'failed' | 'skipped'; error: string | null; to_address: string | null; title: string; period: string | null; created_at: string; sent_at: string | null }
type Info = { email_mode: string; contact_email: string | null; sending_ready: boolean; counts: { sent: number; not_sent: number; waiting: number }; emails: EmailRow[] }
type Preview = { subject?: string; html?: string; to?: string | null; from_name?: string; empty?: boolean; reason?: string }

const STATUS: Record<EmailRow['status'], { label: string; tone?: 'pos' | 'warn' | 'neg' }> = {
  sent: { label: 'Sent', tone: 'pos' }, pending: { label: 'Waiting' }, skipped: { label: 'Not sent', tone: 'warn' }, failed: { label: 'Failed', tone: 'neg' },
}

/** Inside a client: what was emailed, and a preview of what will be. Every email links into their portal. */
export function SubscriberComms({ sub }: { sub: Subscriber }) {
  const info = useApi<Info>(`/api/subscribers/${sub.id}/emails`)
  const toast = useToast()
  const [preview, setPreview] = useState<Preview | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const d = info.data
  const showPreview = async (kind: 'recommendation' | 'monthly') => {
    setBusy(kind)
    try { setPreview(await api<Preview>(`/api/subscribers/${sub.id}/email-preview?kind=${kind}`)) } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(null) }
  }
  const retry = async (id: string) => {
    try { await post(`/api/notifications/${id}/retry`, {}); toast('Queued to send again', 'pos'); setTimeout(info.reload, 1500) } catch (e) { toast((e as Error).message, 'neg') }
  }

  return (
    <section className="comms">
      <div className="comms-head">
        <h3>Emails</h3>
        {d && <span className="muted">{d.counts.sent} sent · {d.counts.not_sent} not sent{d.counts.waiting ? ` · ${d.counts.waiting} waiting` : ''}</span>}
      </div>
      {!d ? <span className="muted">Loading</span> : (
        <>
          {!d.sending_ready && <Banner tone="warn" title="Email sending is not set up yet">Emails are written and recorded as not sent. Nothing reaches {sub.name} until SPEC turns sending on.</Banner>}
          {d.sending_ready && !d.contact_email && <Banner tone="warn" title="No contact email">Add one above, or nothing can be sent.</Banner>}

          <div className="row-tight">
            <Button variant="secondary" onClick={() => showPreview('recommendation')} disabled={busy !== null}><Icon of={Eye} /> {busy === 'recommendation' ? 'Loading' : 'Preview recommendation email'}</Button>
            <Button variant="secondary" onClick={() => showPreview('monthly')} disabled={busy !== null}><Icon of={Eye} /> {busy === 'monthly' ? 'Loading' : 'Preview monthly summary'}</Button>
          </div>
          {preview && (
            <div className="comms-preview">
              {preview.empty ? <p className="muted" style={{ margin: 0 }}>{preview.reason}</p> : (
                <>
                  <div className="comms-preview-head">
                    <span><strong>{preview.subject}</strong><span className="muted"> from {preview.from_name}{preview.to ? ` to ${preview.to}` : ''}</span></span>
                    <Button variant="ghost" onClick={() => setPreview(null)}>Close</Button>
                  </div>
                  <iframe title="Email preview" sandbox="allow-same-origin" srcDoc={preview.html} className="comms-frame" />
                </>
              )}
            </div>
          )}

          {d.emails.length > 0 && (
            <ul className="comms-list">
              {d.emails.map((e) => (
                <li key={e.id}>
                  <Chip tone={STATUS[e.status].tone} dot>{STATUS[e.status].label}</Chip>
                  <span className="comms-item">
                    <span>{e.kind === 'monthly_summary' ? `Monthly summary${e.period ? `, ${e.period}` : ''}` : e.title}</span>
                    <span className="muted">{e.sent_at ? `sent ${ago(e.sent_at)}` : `written ${ago(e.created_at)}`}{e.error ? `. ${e.error}` : ''}</span>
                  </span>
                  {(e.status === 'failed' || e.status === 'skipped') && d.sending_ready && <Button variant="ghost" onClick={() => retry(e.id)}>Send again</Button>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  )
}
